import React, {useEffect} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {IUser} from "../../../component/user/user-interfaces";
import {Tabs, TabsProps} from "antd";
import {
    BankOutlined,
    ReadOutlined,
    SolutionOutlined,
    TeamOutlined,
    UserOutlined
} from "@ant-design/icons";

import "../../../styles/component/employee/create/index.scss";
import {useBaseComponent} from "../../../base/base-component";
import PrimaryDetail from "./primary-detail/primary-detail";
import ContactDetail from "./contact-detail/contact-detail";
import Qualification from "../create/qualification/qualification";
import Experience from "../create/experience-detail/experience";
import FamilyDetail from "../create/family-detail/family-detail";
import BankDetails from "../create/bank-detail/bank-detail";
import {Phone} from "lucide-react";

const sectionToTab: Record<string, string> = {
    primary: '1',
    contact: '2',
    qualification: '3',
    experience: '4',
    family: '5',
    banking: '6',
};

const tabToSection: Record<string, string> = {
    "1": "primary",
    "2": "contact",
    "3": "qualification",
    "4": "experience",
    "5": "family",
    "6": "banking",
};

interface IState {
    internal: { activeTab: string };
    external: { employeeId: string };
}

const EmployeeCreate = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {empId, record} = (location.state ?? {}) as { empId: string; record?: IUser };
    const section = location.pathname.split("/").pop();
    const initialTab = section && sectionToTab[section] ? sectionToTab[section] : '1';

    const {state, updateState} = useBaseComponent<{}, IState>({}, {
        initialState: () => ({
            internal: {
                activeTab: initialTab
            },
            external: {
                employeeId: empId
            }
        })
    })

    const updateEmployeeId = (empId: string) => {
        updateState('external.employeeId', empId)
    };

    useEffect(() => {
        const section = location.pathname.split("/").pop();
        if (section && sectionToTab[section]) {
            updateState('internal.activeTab', sectionToTab[section])
        }
    }, [location.pathname]);

    const replaceSectionInUrl = (newSection: string) => {
        const pathParts = location.pathname.split("/");
        pathParts[pathParts.length - 1] = newSection;
        const newPath = pathParts.join("/");
        navigate(newPath, {replace: true, state: {empId: state.external.employeeId}});
    };

    const handleTabChange = (key: string) => {
        updateState('internal.activeTab', key);
        if (location.pathname.includes('edit')) {
            const section = tabToSection[key];
            replaceSectionInUrl(section);
        }
    };

    const goToNextTab = () => {
        const currentIndex = parseInt(state.internal.activeTab, 10);
        const nextIndex = currentIndex + 1;
        updateState('internal.activeTab', nextIndex.toString());
        const section = tabToSection[nextIndex];
        replaceSectionInUrl(section);
    }

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: 'Primary Details',
            icon: <UserOutlined/>,
            disabled: false,
            children:
                <PrimaryDetail
                    goToNextTab={goToNextTab}
                    employeeId={state.external.employeeId}
                    updateEmployeeId={updateEmployeeId}
                    user={record}
                />,
        },
        {
            key: '2',
            label: 'Contact',
            disabled: !state.external.employeeId,
            icon: <Phone className={'employee-create-lucide contact-icon'}/>,
            children:
                <ContactDetail
                    goToNextTab={goToNextTab}
                    employeeId={state.external.employeeId}
                />,
        },
        {
            key: '3',
            label: 'Qualification',
            disabled: !state.external.employeeId,
            icon: <ReadOutlined/>,
            children:
                <Qualification
                    goToNextTab={goToNextTab}
                    employeeId={state.external.employeeId}
                />,
        },
        {
            key: '4',
            label: 'Experience',
            disabled: !state.external.employeeId,
            icon: <SolutionOutlined/>,
            children:
                <Experience
                    goToNextTab={goToNextTab}
                    employeeId={state.external.employeeId}
                />,
        },
        {
            key: '5',
            label: 'Family Details',
            disabled: !state.external.employeeId,
            icon: <TeamOutlined/>,
            children:
                <FamilyDetail
                    goToNextTab={goToNextTab}
                    employeeId={state.external.employeeId}
                />,
        },
        {
            key: '6',
            label: 'Bank Details',
            disabled: !state.external.employeeId,
            icon: <BankOutlined/>,
            children: <BankDetails employeeId={state.external.employeeId}/>,
        },
    ];

    return (
        <div className="hrms__employee-create">
            <Tabs
                activeKey={state.internal.activeTab}
                onChange={handleTabChange}
                items={items}
            />
        </div>
    )
};

export default EmployeeCreate;
